import { ChevronsLeft, ChevronsRight, CircleHelp, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { navigationItems } from '@/config/navigation';
import { useTheme } from '@/providers/ThemeProvider';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleSidebar } from '@/store/uiSlice';
import { cn } from '@/utils/cn';
import '@/components/layout/aside/AdminAside.scss';

export function AdminAside() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const sidebarOpen = useAppSelector((state) => state.ui.sidebarOpen);

  return (
    <aside className={cn('admin-aside', !sidebarOpen && 'admin-aside--collapsed')}>
      <div className="admin-aside__logo-wrap" aria-hidden="true">
        <img src={theme.logos.sidebar} alt={`${theme.brandName} logo`} className="admin-aside__logo" />
      </div>
      <nav className="admin-aside__nav" aria-label="Primary navigation">
        {navigationItems.map(({ to, labelKey, icon: Icon, disabled, disabledLabelKey }) => {
          const content = (
            <>
              <Icon size={18} />
              <div className="admin-aside__meta">
                <span>{t(labelKey)}</span>
                {disabled && disabledLabelKey ? (
                  <small className="admin-aside__disabled-note">{t(disabledLabelKey)}</small>
                ) : null}
              </div>
            </>
          );

          if (disabled) {
            return (
              <span
                key={to}
                className="admin-aside__link admin-aside__link--disabled"
                aria-disabled="true"
                title={t(disabledLabelKey ?? 'sidebar.nav.comingSoon')}
              >
                {content}
              </span>
            );
          }

          return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn('admin-aside__link', isActive && 'admin-aside__link--active')
              }
              end={to === '/'}
            >
              {content}
            </NavLink>
          );
        })}
      </nav>
      <div className="admin-aside__footer" aria-label="Sidebar utilities">
        <div className="admin-aside__footer-links">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              cn('admin-aside__footer-link', isActive && 'admin-aside__footer-link--active')
            }
          >
            <Settings size={18} />
            <span>{t('Settings')}</span>
          </NavLink>
          <button type="button" className="admin-aside__footer-link">
            <CircleHelp size={18} />
            <span>{t('Help & Support')}</span>
          </button>
        </div>

        <button
          type="button"
          className="admin-aside__collapse-toggle"
          onClick={() => dispatch(toggleSidebar())}
          aria-label={sidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          {sidebarOpen ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
          <span className="admin-aside__collapse-toggle-label">
            {sidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')}
          </span>
        </button>
      </div>
    </aside>
  );
}
